import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import { FiUsers, FiSearch, FiFilter, FiExternalLink, FiPlus, FiMoreHorizontal, FiCalendar, FiGlobe } from 'react-icons/fi';

export default function AdminClients() {
  const { adminUser } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await adminApi.getClients(adminUser.admin_session_token);
        if (res.success) setClients(res.clients || []);
      } catch (e) {
        console.error('Error fetching clients:', e);
      } finally { 
        setLoading(false); 
      }
    };
    fetchClients();
  }, [adminUser]);

  const filtered = clients.filter(c => {
    const matchesSearch = `${c.client_name} ${c.business_name} ${c.website_domain}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) return <div className="animate-pulse space-y-8">
    <div className="h-10 w-48 bg-slate-200 rounded-lg"></div>
    <div className="h-[500px] bg-white rounded-[2.5rem]"></div>
  </div>;

  return (
    <div className="space-y-8">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tighter">Client Portfolio</h1>
          <p className="text-slate-500 font-bold text-sm mt-1 uppercase tracking-widest opacity-60">Managed Access Points</p>
        </div>
        <button 
          onClick={() => navigate('/admin/clients/new')}
          className="px-8 py-4 bg-brand-blue text-white rounded-2xl font-black uppercase tracking-widest text-[10px] flex items-center gap-3 shadow-xl shadow-brand-blue/30 hover:-translate-y-1 transition-all"
        >
          <SafeIcon icon={FiPlus} /> Provision Client
        </button>
      </header>

      <div className="bg-white rounded-[1.5rem] p-3 shadow-sm border border-slate-100 flex flex-col lg:flex-row items-center gap-3">
        <div className="w-full lg:flex-grow relative group">
          <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-slate-300 group-focus-within:text-brand-blue transition-colors">
            <SafeIcon icon={FiSearch} />
          </div>
          <input 
            type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search by name, business or domain..." 
            className="w-full pl-12 pr-4 py-3.5 bg-slate-50 rounded-xl text-xs font-bold outline-none border-2 border-transparent focus:border-brand-blue/10 transition-all"
          />
        </div>
        <div className="w-full lg:w-auto flex items-center gap-2 bg-slate-50 p-1 rounded-xl">
          {['All', 'Trial', 'Active', 'Grace', 'Expired'].map(s => (
            <button 
              key={s} onClick={() => setStatusFilter(s)}
              className={`px-6 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${statusFilter === s ? 'bg-white text-brand-blue shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[1000px]">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100">
                <th className="py-6 px-8 text-[10px] font-black text-slate-400 uppercase tracking-widest">Business Partner</th>
                <th className="py-6 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Digital Domain</th>
                <th className="py-6 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                <th className="py-6 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Expiry</th>
                <th className="py-6 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Leads</th>
                <th className="py-6 px-8 text-right text-[10px] font-black text-slate-400 uppercase tracking-widest">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map(client => (
                <tr key={client.client_key} className="group hover:bg-slate-50/80 transition-all">
                  <td className="py-6 px-8">
                    <div className="flex flex-col">
                      <span className="text-xs font-black text-slate-900">{client.business_name}</span>
                      <span className="text-[10px] font-bold text-slate-400 uppercase mt-0.5">{client.client_name}</span>
                    </div>
                  </td>
                  <td className="py-6 px-6">
                    <div className="flex items-center gap-2 text-brand-blue font-bold text-xs">
                      <SafeIcon icon={FiGlobe} /> {client.website_domain}
                    </div>
                  </td>
                  <td className="py-6 px-6">
                    <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${
                      client.status === 'Active' ? 'bg-emerald-50 text-emerald-600' :
                      client.status === 'Trial' ? 'bg-brand-blue/5 text-brand-blue' :
                      client.status === 'Grace' ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'
                    }`}>
                      {client.status}
                    </span>
                  </td>
                  <td className="py-6 px-6">
                    <div className="flex flex-col">
                       <span className="text-xs font-bold text-slate-900">{client.service_end_date || 'Ongoing'}</span>
                       {client.days_remaining !== undefined && (
                         <span className={`text-[9px] font-black uppercase mt-0.5 ${client.days_remaining < 5 ? 'text-red-400' : 'text-slate-400'}`}>
                           {client.days_remaining} Days Left
                         </span>
                       )}
                    </div>
                  </td>
                  <td className="py-6 px-6 text-center">
                    <span className="text-sm font-black text-slate-900">{client.total_leads || 0}</span>
                  </td>
                  <td className="py-6 px-8 text-right">
                    <button 
                      onClick={() => navigate(`/admin/clients/${client.client_key}`)}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-100 group-hover:bg-navy-900 group-hover:text-white text-slate-600 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all"
                    >
                      <SafeIcon icon={FiExternalLink} /> Configure
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="py-20 text-center">
             <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-slate-100"><SafeIcon icon={FiUsers} className="text-2xl text-slate-200" /></div>
             <h4 className="text-lg font-black text-slate-900 uppercase tracking-tighter">No Clients Found</h4>
             <p className="text-slate-400 text-xs font-medium mt-1">Refine your filters or search terms</p>
          </div>
        )}
      </div>
    </div>
  );
}